import { Button, Modal, Space, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import client from '@/lib/feathers';
import type CommunityGroups from '@/types/database/communityGroups';

import AddAndEditCommunityModal from './AddAndEditCommunityModal';
import CheckDetailModal from './CheckDetailModal';

const { confirm } = Modal;

const showDeleteCommunityConfirm = (
  id: string,
  updateCommunityGroups: () => void
) => {
  confirm({
    title: '確定刪除該社區群組嗎？',
    content: '確定請按確認鍵',
    icon: <ExclamationCircleOutlined />,
    onOk: async () => {
      try {
        await client.service('communityGroups').remove(id);
        updateCommunityGroups();
        message.success('刪除社區群組成功!');
      } catch (error) {
        message.error('刪除社區群組失敗!');
      }
    },
  });
};

export const getCommunityTableColumns = (updateCommunityGroups: () => void) => {
  const columns: ColumnsType<CommunityGroups> = [
    {
      title: '社區名稱',
      dataIndex: 'name',
    },
    {
      title: '地址',
      dataIndex: 'address',
    },
    {
      title: '建立時間',
      dataIndex: 'createdTime',
      render: (_value, record) =>
        dayjs(record.createdTime).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '更新時間',
      dataIndex: 'updatedTime',
      render: (_value, record) =>
        dayjs(record.updatedTime).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      dataIndex: 'actions',
      fixed: 'right',
      width: 200,
      render: (_value, record) => {
        return (
          <Space>
            <AddAndEditCommunityModal
              formType="edit"
              updateCommunityGroups={updateCommunityGroups}
              record={record}
            />
            <Button
              type="primary"
              danger
              onClick={() =>
                showDeleteCommunityConfirm(record._id, updateCommunityGroups)
              }
            >
              刪除
            </Button>
          </Space>
        );
      },
    },
  ];

  return columns;
};

export const getHouseholdNumbersInfoTableColumns = () => {
  const householdNumbersInfoTableColumns: ColumnsType<CommunityGroups> = [
    {
      title: '社區',
      dataIndex: 'name',
    },
    {
      title: '戶號',
      dataIndex: 'householdNumbers',
      ellipsis: true,
      render: (value) => value.join('、'),
    },
    {
      title: '操作',
      dataIndex: 'actions',
      fixed: 'right',
      width: 250,
      render: (_value, record) => {
        return <CheckDetailModal record={record} />;
      },
    },
  ];

  return householdNumbersInfoTableColumns;
};

const checkIsIncludedCommunityName = (args: {
  communityName: string;
  dataType: 'selectOptions' | 'tableDataSource';
  communityNames?: { label: string; value: string }[];
  communities?: CommunityGroups[];
}) => {
  const { communityNames, communityName, dataType, communities } = args;
  if (dataType === 'selectOptions') {
    const isIncludedCommunityName = communityNames?.some(
      (communityNameObject) => communityNameObject.value === communityName
    );
    return isIncludedCommunityName;
  }

  const isIncludedCommunityName = communities?.some(
    (community) => community.name === communityName
  );
  return isIncludedCommunityName;
};

export const getCommunityNames = (communityGroups: CommunityGroups[]) => {
  const communityNames = communityGroups.reduce(
    (communityNames: { label: string; value: string }[], community) => {
      const isIncludedCommunityName = checkIsIncludedCommunityName({
        communityNames,
        communityName: community.name,
        dataType: 'selectOptions',
      });
      if (isIncludedCommunityName === false && community.name !== undefined) {
        communityNames.push({
          label: community.name,
          value: community.name,
        });
      }
      return communityNames;
    },
    []
  );
  return communityNames;
};

export const getCommunityGroupsData = (
  communityGroups: CommunityGroups[],
  communityName: string | undefined
) => {
  if (communityName) {
    const result = communityGroups.filter(
      (community) => community.name === communityName
    );
    return result;
  }

  return communityGroups;
};
